import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Badge } from "@/shared/ui/badge";
import { Tabs, TabsContent } from "@/shared/ui/tabs";
import { useToast } from "@/shared/hooks/use-toast";
import { Upload, CheckCircle } from "lucide-react";
import Papa from "papaparse";
import { supabase } from "@/shared/integrations/supabase/client";
import { requireUserIdForDatabase } from "@/shared/lib/userIdUtils";
import { FeatureLockOverlay } from "@/shared/components/FeatureLockOverlay";

import { UploadStep } from "./propertyImportModal/UploadStep";
import { MappingStep } from "./propertyImportModal/MappingStep";
import { CompleteStep } from "./propertyImportModal/CompleteStep";
import {
  initializeColumnMapping,
  type ImportError,
  type ParsedProperty,
} from "./propertyImportModal/fields";
import { validatePropertyRows } from "./propertyImportModal/validation";

interface PropertyImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId?: string;
  onComplete?: () => void;
}

export function PropertyImportModal({
  open,
  onOpenChange,
  projectId,
  onComplete,
}: PropertyImportModalProps) {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedProperty[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importComplete, setImportComplete] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);

  const parseFile = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setParsedData(results.data as ParsedProperty[]);
        setColumnMapping(initializeColumnMapping(results.meta.fields || []));
        setCurrentStep(2);
      },
      error: (error) => {
        toast({
          title: "Parse Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleFileSelected = (selectedFile: File) => {
    setFile(selectedFile);
    parseFile(selectedFile);
  };

  const validateData = () => {
    const newErrors = validatePropertyRows({ parsedData, columnMapping });
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleImport = async () => {
    if (!validateData()) {
      toast({
        title: "Validation Failed",
        description: `Found ${errors.length} validation errors`,
        variant: "destructive",
      });
      return;
    }

    setImporting(true);
    setProgress(0);
    let imported = 0;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      for (let i = 0; i < parsedData.length; i++) {
        const row = parsedData[i];

        const propertyData: any = {
          created_by: requireUserIdForDatabase(),
          name: row[columnMapping.name],
          address: row[columnMapping.address],
          property_type: row[columnMapping.property_type],
          city: row[columnMapping.city] || null,
          state: row[columnMapping.state] || null,
          zip_code: row[columnMapping.zip_code] || null,
          country: row[columnMapping.country] || "United States",
          latitude: row[columnMapping.latitude] ? parseFloat(row[columnMapping.latitude]) : null,
          longitude: row[columnMapping.longitude] ? parseFloat(row[columnMapping.longitude]) : null,
          property_sub_type: row[columnMapping.property_sub_type] || null,
          property_description: row[columnMapping.property_description] || null,
          square_footage: row[columnMapping.square_footage]
            ? parseInt(row[columnMapping.square_footage])
            : null,
          year_built: row[columnMapping.year_built]
            ? parseInt(row[columnMapping.year_built])
            : null,
          number_of_units: row[columnMapping.number_of_units]
            ? parseInt(row[columnMapping.number_of_units])
            : null,
          facility_image_url: row[columnMapping.facility_image_url] || null,
        };

        if (projectId && projectId !== "unassigned") {
          propertyData.organization_id = projectId;
        }

        const { error } = await supabase.from("properties").insert(propertyData);

        if (!error) {
          imported++;
        }

        setProgress(((i + 1) / parsedData.length) * 100);
      }

      setSuccessCount(imported);
      setImportComplete(true);
      setCurrentStep(3);

      toast({
        title: "Import Complete",
        description: `Successfully imported ${imported} of ${parsedData.length} properties`,
      });

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error("Import error:", error);
      toast({
        title: "Import Failed",
        description: "An error occurred during import",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setFile(null);
    setParsedData([]);
    setColumnMapping({});
    setErrors([]);
    setImporting(false);
    setProgress(0);
    setImportComplete(false);
    setSuccessCount(0);
    setCurrentStep(1);
  };

  const handleClose = () => {
    if (!importing) {
      resetImport();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={importing ? undefined : handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Upload className="h-5 w-5" />
            Bulk Import Properties
            {projectId && projectId !== "unassigned" && (
              <Badge variant="outline">For Project</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* MVP2-282: Modal content with feature lock overlay */}
        <div className="relative">
          {/* Progress Steps */}
          <div className="flex items-center space-x-4 mb-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step <= currentStep
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {step === 3 && importComplete ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    step
                  )}
                </div>
                {step < 3 && (
                  <div
                    className={`w-8 h-0.5 ${
                      step < currentStep ? "bg-primary" : "bg-muted"
                    }`}
                  />
                )}
              </div>
            ))}
            <div className="text-sm text-muted-foreground ml-2">
              {currentStep === 1 && "Upload File"}
              {currentStep === 2 && "Review & Map"}
              {currentStep === 3 && "Import Complete"}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <Tabs value={currentStep.toString()} className="space-y-4">
              <TabsContent value="1">
                <UploadStep
                  file={file}
                  parsedDataLength={parsedData.length}
                  onFileSelected={handleFileSelected}
                />
              </TabsContent>

              <TabsContent value="2">
                <MappingStep
                  parsedData={parsedData}
                  columnMapping={columnMapping}
                  setColumnMapping={setColumnMapping}
                  errors={errors}
                  importing={importing}
                  progress={progress}
                  onBack={() => setCurrentStep(1)}
                  onValidate={validateData}
                  onImport={handleImport}
                />
              </TabsContent>

              <TabsContent value="3">
                <CompleteStep
                  successCount={successCount}
                  totalCount={parsedData.length}
                  onReset={resetImport}
                  onClose={handleClose}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* MVP2-282: Feature lock overlay */}
          <FeatureLockOverlay />
        </div>
      </DialogContent>
    </Dialog>
  );
}
